import { LogOut, Settings } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ProfileDialog } from "@/components/ProfileDialog";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/branding";

const ROLE_LABEL = { patient: "Paciente", professional: "Profesional", admin: "Admin" };

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="TransSalud" className="h-9 w-9 object-contain" />
          <span className="font-heading font-semibold text-lg">
            TransSalud <span className="text-primary">· THAG</span>
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {user?.role === "patient" && (
            <Button
              onClick={() => navigate("/nueva-solicitud")}
              className="rounded-full hidden sm:inline-flex"
              data-testid="navbar-new-request-btn"
            >
              Nueva solicitud
            </Button>
          )}
          {user && <NotificationsBell />}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full" data-testid="navbar-user-menu">
                  <Avatar className="h-9 w-9 border">
                    <AvatarImage src={user.picture || undefined} />
                    <AvatarFallback className="bg-accent text-sm font-semibold">
                      {(user.name || "U").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">
                    {ROLE_LABEL[user.role] || "Sin rol"} · {user.email}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="navbar-profile-btn">
                  <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} /> Mi perfil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="navbar-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </header>
  );
}
